import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Zap, TrendingUp, Wallet, ShieldCheck, ArrowRight, BarChart3, Users, Receipt,
  CheckCircle2, Store, Building2, Banknote, Sparkles, PlayCircle,
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/8360497/pexels-photo-8360497.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const CREATOR_IMG = "https://images.pexels.com/photos/13929970/pexels-photo-13929970.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const ANALYTICS_IMG = "https://images.pexels.com/photos/5833762/pexels-photo-5833762.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200";
const PAY_IMG = "https://images.pexels.com/photos/6694563/pexels-photo-6694563.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

function Section({ children, className = "" }) {
  return <section className={`max-w-6xl mx-auto px-4 sm:px-6 ${className}`}>{children}</section>;
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden" data-testid="landing-container">
      {/* NAV */}
      <nav className="sticky top-0 z-40 border-b border-border bg-background/70 backdrop-blur-xl">
        <Section className="h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Komisia" className="h-9 w-9 rounded-lg shadow-lg shadow-primary/30" />
            <div>
              <p className="font-display font-extrabold text-sm leading-none">Komisia</p>
              <p className="text-[10px] text-muted-foreground mt-1">Affiliate & Disbursement</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#fitur" className="hover:text-foreground transition-colors">Fitur</a>
            <a href="#cara-kerja" className="hover:text-foreground transition-colors">Cara Kerja</a>
            <a href="#revenue" className="hover:text-foreground transition-colors">Model Bisnis</a>
          </div>
          <Link to="/login">
            <Button data-testid="landing-login-button" className="bg-primary hover:bg-primary/90 text-white font-semibold">
              Masuk Talent <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </Link>
        </Section>
      </nav>

      {/* HERO */}
      <div className="grid-glow">
        <Section className="pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div variants={fadeUp} initial="hidden" animate="show" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-xs font-medium mb-6">
              <Sparkles className="h-3.5 w-3.5" /> Platform afiliasi #1 untuk agency & talent Indonesia
            </motion.div>
            <motion.h1 variants={fadeUp} custom={1} initial="hidden" animate="show" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Ubah penjualan afiliasi jadi <span className="text-primary">komisi yang cair otomatis</span>.
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} initial="hidden" animate="show" className="text-base sm:text-lg text-muted-foreground mt-6 max-w-xl leading-relaxed">
              Komisia menghubungkan brand, talent kreator, dan pencairan dana dalam satu sistem —
              tracking GMV real-time, perhitungan komisi transparan, dan disbursement ke bank & e-wallet.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} initial="hidden" animate="show" className="flex flex-wrap items-center gap-4 mt-8">
              <Link to="/login?daftar=1">
                <Button data-testid="landing-hero-cta" size="lg" className="bg-primary hover:bg-primary/90 text-white font-semibold h-12 px-7 text-base">
                  Mulai Sekarang <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </Link>
              <a href="#cara-kerja">
                <Button variant="outline" size="lg" className="border-border h-12 px-7 text-base">
                  <PlayCircle className="h-5 w-5 mr-2" /> Lihat Cara Kerja
                </Button>
              </a>
            </motion.div>
            <motion.div variants={fadeUp} custom={4} initial="hidden" animate="show" className="grid grid-cols-3 gap-6 mt-12 max-w-md">
              {[["Rp 34,7 jt+", "GMV terlacak"], ["80%", "Bagi hasil talent"], ["< 5 mnt", "Proses payout"]].map(([v, l]) => (
                <div key={l}>
                  <p className="font-display text-2xl font-bold text-foreground">{v}</p>
                  <p className="text-xs text-muted-foreground mt-1">{l}</p>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative">
            <div className="absolute -inset-4 bg-primary/20 blur-3xl rounded-full opacity-40" />
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-2xl">
              <img src={HERO_IMG} alt="Kreator konten afiliasi" className="w-full h-[420px] lg:h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="absolute -left-4 bottom-10 glass border border-border rounded-xl p-4 shadow-xl w-52">
              <div className="flex items-center gap-2 mb-1">
                <div className="h-8 w-8 rounded-lg bg-emerald-500/20 grid place-items-center"><CheckCircle2 className="h-4 w-4 text-emerald-400" /></div>
                <p className="text-xs text-muted-foreground">Komisi cair</p>
              </div>
              <p className="font-display text-lg font-bold text-emerald-400">Rp 1.480.000</p>
              <p className="text-[10px] text-muted-foreground font-mono"> → BCA</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="absolute -right-2 top-8 glass border border-border rounded-xl p-4 shadow-xl">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-cyan-400" />
                <div>
                  <p className="text-[10px] text-muted-foreground">GMV bulan ini</p>
                  <p className="font-display font-bold text-cyan-400">+ 24,8%</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </Section>
      </div>

      {/* LOGOS / PLATFORMS */}
      <Section className="pb-16">
        <p className="text-center text-xs text-muted-foreground uppercase tracking-widest mb-6">Terhubung dengan platform afiliasi favorit</p>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-muted-foreground font-display font-bold text-lg opacity-70">
          {["TikTok Shop", "Shopee Affiliate", "Tokopedia", "Lazada"].map((p) => (
            <span key={p} className="hover:text-foreground transition-colors">{p}</span>
          ))}
        </div>
      </Section>

      {/* FEATURES */}
      <Section className="py-16" >
        <div id="fitur" className="scroll-mt-20" />
        <div className="max-w-2xl mb-12">
          <p className="text-primary font-semibold text-sm mb-3">FITUR UTAMA</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Semua yang dibutuhkan agency afiliasi modern</h2>
          <p className="text-muted-foreground mt-4">Dari onboarding talent hingga uang mendarat di rekening — semua otomatis dan transparan.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: Users, t: "Manajemen Talent", d: "Kelola kreator, tier komisi, dan detail rekening/e-wallet pencairan dalam satu tempat.", c: "#6366F1" },
            { icon: BarChart3, t: "Tracking Penjualan", d: "Pantau GMV & order afiliasi per talent dan per platform secara real-time.", c: "#06B6D4" },
            { icon: Receipt, t: "Komisi Otomatis", d: "Hitung komisi & margin agency otomatis tiap transaksi — nol perhitungan manual.", c: "#F59E0B" },
            { icon: Wallet, t: "Disbursement", d: "Cairkan komisi ke bank & e-wallet talent langsung dengan bukti transfer.", c: "#10B981" },
            { icon: ShieldCheck, t: "Model Revenue Transparan", d: "Simulator margin & breakdown pendapatan yang jelas dan auditable.", c: "#A5B4FC" },
            { icon: TrendingUp, t: "Laporan & Export", d: "Ekspor CSV/PDF untuk rekonsiliasi, pajak, dan audit kapan saja.", c: "#F43F5E" },
          ].map((f, i) => (
            <motion.div key={f.t} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="bg-card border border-border rounded-xl p-6 hover:border-gray-700 transition-colors group">
              <div className="h-12 w-12 rounded-xl grid place-items-center mb-4 transition-transform group-hover:scale-110" style={{ background: `${f.c}22` }}>
                <f.icon className="h-6 w-6" style={{ color: f.c }} />
              </div>
              <h3 className="font-semibold text-lg">{f.t}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <div className="bg-[#0d121c] border-y border-border">
        <Section className="py-20">
          <div id="cara-kerja" className="scroll-mt-20" />
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden border border-border shadow-2xl">
                <img src={ANALYTICS_IMG} alt="Dashboard analitik" className="w-full h-[380px] object-cover" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-primary font-semibold text-sm mb-3">CARA KERJA</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Empat langkah, komisi mendarat</h2>
              <div className="mt-8 space-y-6">
                {[
                  { icon: Store, t: "1. Hubungkan TikTok Shop & Promosi", d: "Tautkan akun TikTok Shop di portal kreator untuk tracking otomatis keranjang kuning & link bio." },
                  { icon: BarChart3, t: "2. Penjualan tercatat otomatis", d: "Setiap order & GMV masuk ke dashboard dan komisi dihitung instan." },
                  { icon: Building2, t: "3. Agency ambil margin", d: "Komisia menahan management fee, sisanya untuk talent." },
                  { icon: Banknote, t: "4. Disburse", d: "Komisi ditransfer ke rekening talent dengan bukti transfer lengkap." },
                ].map((s, i) => (
                  <motion.div key={s.t} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="flex gap-4">
                    <div className="h-11 w-11 rounded-lg bg-primary/15 border border-primary/25 grid place-items-center shrink-0">
                      <s.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">{s.t}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">{s.d}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* REVENUE MODEL */}
      <Section className="py-20">
        <div id="revenue" className="scroll-mt-20" />
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-emerald-400 font-semibold text-sm mb-3">MODEL BISNIS TRANSPARAN</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Bagaimana kami menghasilkan pendapatan</h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Merchant membayar komisi dari GMV. Komisi ini dibagi antara talent dan agency dengan proporsi yang jelas —
              tidak ada biaya tersembunyi.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { l: "Komisi merchant", v: "10% dari GMV", c: "#06B6D4", w: "100%" },
                { l: "Bagi hasil talent", v: "80% dari pool komisi", c: "#F59E0B", w: "80%" },
                { l: "Margin agency (platform + management fee)", v: "20% dari pool komisi", c: "#10B981", w: "20%" },
              ].map((r) => (
                <div key={r.l}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-muted-foreground">{r.l}</span>
                    <span className="font-mono font-semibold" style={{ color: r.c }}>{r.v}</span>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <motion.div initial={{ width: 0 }} whileInView={{ width: r.w }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="h-full rounded-full" style={{ background: r.c }} />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-6 bg-card border border-border rounded-lg p-4">
              Contoh: order GMV Rp 1.000.000 → pool komisi Rp 100.000 → talent menerima Rp 80.000, agency Rp 20.000 (dikurangi biaya Rp 3.000).
            </p>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden border border-border shadow-2xl">
              <img src={PAY_IMG} alt="Pencairan komisi" className="w-full h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-background/60 to-transparent" />
            </div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section className="pb-24">
        <div className="relative rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/15 via-card to-card p-10 lg:p-16 text-center overflow-hidden">
          <div className="absolute -top-20 -right-20 h-64 w-64 bg-primary/30 blur-3xl rounded-full" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl mx-auto">
              Siap kelola afiliasi & cairkan komisi tanpa ribet?
            </h2>
            <p className="text-muted-foreground mt-4 max-w-xl mx-auto">
              Gabung sebagai talent Komisia dan rasakan pencairan komisi yang cepat, transparan, dan otomatis.
            </p>
            <Link to="/login?daftar=1">
              <Button data-testid="landing-cta-button" size="lg" className="mt-8 bg-primary hover:bg-primary/90 text-white font-semibold h-12 px-8 text-base">
                Daftar Jadi Talent <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="border-t border-border">
        <Section className="py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Komisia" className="h-8 w-8 rounded-lg" />
            <p className="font-display font-bold text-sm">Komisia</p>
          </div>
          <p className="text-xs text-muted-foreground">PT Digital Media Inovasi © 2026 Komisia. All rights reserved.</p>
        </Section>
      </footer>
    </div>
  );
}
